import React, { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import './LivingBoardPreview.scss';

/**
 * The one signature visual on the login page: a small, ambient kanban board
 * where a ticket actually glides from "In Progress" into "Done" on a loop,
 * with a matching activity caption underneath. Not a stock illustration -
 * literally the product's own core interaction, used as the hero.
 */

const STATIC_CARDS = {
  todo: [{ key: 'AE-118', title: 'Design empty states', accent: '#6aa9fa' }],
  inProgress: [{ key: 'AE-131', title: 'Fix flaky e2e test', accent: '#f5b942' }],
  done: [{ key: 'AE-109', title: 'Ship invite flow', accent: '#22c58c' }],
};

const TRAVELING_CARD = { key: 'AE-142', title: 'Sprint 14 retro notes', accent: '#f5b942' };

const ACTIVITY_LINES = [
  'Priya moved AE-142 to Done',
  'Sam started Sprint 14',
  '2 blockers cleared this morning',
  'Team velocity: 34 points',
];

export const LivingBoardPreview: React.FC = () => {
  const [tick, setTick] = useState(0);

  useEffect(() => {
    const id = setInterval(() => setTick((t) => t + 1), 6000);
    return () => clearInterval(id);
  }, []);

  return (
    <Box className="living-board" aria-hidden="true">
      <Box className="living-board__columns">
        <Box className="living-board__col">
          <Typography className="living-board__col-label">To Do</Typography>
          {STATIC_CARDS.todo.map((c) => (
            <Box key={c.key} className="living-board__card" style={{ ['--card-accent' as any]: c.accent }}>
              <span className="living-board__card-key">{c.key}</span>
              <span className="living-board__card-title">{c.title}</span>
            </Box>
          ))}
        </Box>
        <Box className="living-board__col">
          <Typography className="living-board__col-label">In Progress</Typography>
          {STATIC_CARDS.inProgress.map((c) => (
            <Box key={c.key} className="living-board__card" style={{ ['--card-accent' as any]: c.accent }}>
              <span className="living-board__card-key">{c.key}</span>
              <span className="living-board__card-title">{c.title}</span>
            </Box>
          ))}
          <Box className="living-board__card living-board__card--ghost" />
        </Box>
        <Box className="living-board__col">
          <Typography className="living-board__col-label">Done</Typography>
          {STATIC_CARDS.done.map((c) => (
            <Box key={c.key} className="living-board__card living-board__card--done" style={{ ['--card-accent' as any]: c.accent }}>
              <span className="living-board__card-key">{c.key}</span>
              <span className="living-board__card-title">{c.title}</span>
            </Box>
          ))}
        </Box>

        {/* Absolutely positioned so it can glide across column boundaries */}
        <Box
          key={tick}
          className="living-board__traveling-card"
          style={{ ['--card-accent' as any]: TRAVELING_CARD.accent }}
        >
          <span className="living-board__card-key">{TRAVELING_CARD.key}</span>
          <span className="living-board__card-title">{TRAVELING_CARD.title}</span>
          <span className="living-board__check">✓</span>
        </Box>
      </Box>

      <Box className="living-board__ticker">
        <span key={tick % ACTIVITY_LINES.length} className="living-board__ticker-line">
          <span className="living-board__ticker-dot" />
          {ACTIVITY_LINES[tick % ACTIVITY_LINES.length]}
        </span>
      </Box>
    </Box>
  );
};

export default LivingBoardPreview;
